import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PhaseIntervalApi, PhaseIntervalInput } from '../../../models/phase-interval.model';
import { ProjectsService } from '../../../services/projects.service';
import { ShareWithService } from '../../../services/share-with.service';
import { ObservationsService } from '../../../services/Observations/observations.service';
import { Router } from '@angular/router';
import { RestfulEndpoint } from '../../../services/restful-endpoint.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment/moment';
import {
  ActivityPayload,
  CreatePhaseActivityResponse,
  PhaseActivityListResponse,
  PhasesApi,
  ProgressesApi,
  ProgressLogsBody,
  ProjectPhasesResponseModel,
  ReportPhase,
} from 'app/models/phases.model';
import { FileModel } from '../../../models/files.model';
import { ProgressLogs } from '../../../models/logs.model';
import { MetaData } from '../../../models/shared.model';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { AccessLevel, RoleLevel } from 'app/core/user/user.model';

export interface MappedPhase extends PhasesApi {
  actual_end_date: string;
  actual_start_date: string;
  progressPercentageTmp: number;
  progressPercentage: number;
  lastProgressDate: string;
  progresses: ProgressesApi[];
  phase_name: string;
}

export interface MappedProgress {
  id: string;
  phaseId: string;
  progressDesc: string;
  percentage: number;
  date: Date;
  status: 'in_progress' | 'pending' | 'closed';
  reports: ReportPhase[];
  attached: FileModel[];
  createdBy: string;
  phase: PhasesApi;
  createdAt: Date;
}

export interface UpdatePhaseBody {
  phase_id: string;
  project_id: string;
  phase_name: string;
  phase_desc: string;
  start_date: string;
  planned_cost: number;
  actual_cost: number;
  end_date: string;
  weight: number;
  reports: string[];
  attached: File[];
}

@Injectable({
  providedIn: 'root',
})
export class ProjectPlanningService {
  remainingWeight: number = 0;
  overallPercentage: number;
  projectId: string = '';
  currentPageLog: number = 1;
  totalLogs: number = 1;
  totalActivities: number = 0;
  private state: 'error' | 'noAccess' | 'invalid' | string;
  private phasesSubject = new BehaviorSubject<PhasesApi[]>([]);
  phases$: Observable<PhasesApi[]> = this.phasesSubject.asObservable();
  private phasesActivitySubject = new BehaviorSubject<ActivityPayload[]>([]);
  phasesActivities$: Observable<ActivityPayload[]> = this.phasesActivitySubject.asObservable();
  private progressesSubject = new BehaviorSubject<ProgressesApi[]>([]);
  progresses$: Observable<ProgressesApi[]> = this.progressesSubject.asObservable();
  private progressLogsSubject = new BehaviorSubject<ProgressLogs[]>([]);
  logsLoading: boolean = false;
  phases_meta_data: MetaData;
  project_phases_params = {
    page: 1,
    // temporary work around until we separate graph from phases api
    per_page: 1000,
    sort_by: 'created_at',
    sort_dir: 'asc',
  };

  constructor(
    private _projectsApi: ProjectsService,
    private shareService: ShareWithService,
    private observationsService: ObservationsService,
    private _router: Router,
    private _toaster: ToastrService,
    private _transloco: TranslocoService,
    private endPoint: RestfulEndpoint,
  ) {}

  private _phases: PhasesApi[] = [];

  get phases(): PhasesApi[] {
    return this._phases;
  }

  private _phasesActivities: ActivityPayload[] = [];

  get phasesActivities(): ActivityPayload[] {
    return this._phasesActivities;
  }

  set phasesActivities(val) {
    this._phasesActivities = val;
  }

  private _progresses: ProgressesApi[] = [];

  get progresses(): ProgressesApi[] {
    return this._progresses;
  }

  private _progressLogs: ProgressLogs[] = [];

  get progressLogs(): ProgressLogs[] {
    return this._progressLogs;
  }

  set progressLogs(val) {
    this._progressLogs = val;
  }

  async fetchProgressLogs(body: ProgressLogsBody): Promise<void> {
    this.logsLoading = true;
    body.project_id = this.projectId;
    if (this.totalLogs && this._progressLogs.length >= this.totalLogs) return;

    try {
      const response = await this._projectsApi.getProgressLogs(body).toPromise();
      if (response) {
        this.totalLogs = response.total;
        this._progressLogs = [...this._progressLogs, ...response.data];
        this.progressLogsSubject.next(this._progressLogs);
        this.currentPageLog += 1;
        this.logsLoading = false;
      }
    } catch (error) {
      this._toaster.error(this._transloco.translate('Something went wrong'));
      this.logsLoading = false;
    }
  }

  async fetchPhasesActivities(
    body?: {
      phase_id?: string;
      project_id?: string;
      date?: string;
      sort?: string;
    },
    pageNumber: number = 1,
    pageSize: number = 10,
    onScroll: boolean = false,
  ): Promise<number | PhaseActivityListResponse> {
    try {
      const response: PhaseActivityListResponse = await this.endPoint
        .post('get_project_phase_activities', {
          ...body,
          page: pageNumber,
          per_page: pageSize,
        })
        .toPromise();

      if (response.status) {
        this.totalActivities = response.pagination.total_items;
        if (onScroll) {
          return response;
        } else {
          this._phasesActivities = response.data;
          this.phasesActivitySubject.next(this._phasesActivities);
          return this.totalActivities; // Return the total number of activities
        }
      } else {
        this._toaster.error(response.message);
      }
    } catch (error) {
      this._toaster.error(this._transloco.translate('Something went wrong'));
    }
  }

  async fetchPhases(projectId: string): Promise<void> {
    this.projectId = projectId;
    this._phases =
      (await this._projectsApi
        .getPhases(projectId)
        .toPromise()
        .catch((e: HttpErrorResponse) => {
          if (e.status === 400) {
            this.state = 'invalid';
          } else if (e.status === 403) {
            this.state = 'noAccess';
          } else {
            this.state = 'error';
          }
        })) || [];
    this.phasesSubject.next(this._phases);
  }

  async fetchProgresses(): Promise<void> {
    if (this._phases?.length) {
      const response = await this._projectsApi.getAllProgresses(this.projectId).toPromise();
      const sortedProgresses = response.sort((b, a) => {
        return (
          moment(a.created_at, 'YYYY-MM-DD hh:mm').unix() -
          moment(b.created_at, 'YYYY-MM-DD hh:mm').unix()
        );
      });
      this._progresses = sortedProgresses.map((m) => ({
        ...m,
        phase: this.getPhaseById(m.phase_id),
      }));
      this.progressesSubject.next(this._progresses);
      this.phasesSubject.next(this._phases);
    }
  }

  async createProgress(
    body: any,
    status: 'in_progress' | 'pending' | 'closed' = null,
  ): Promise<ProgressesApi> {
    const response = await this._projectsApi.createProgress(body).toPromise();
    // response.phase = this._phases.find(
    //   (phase) => phase.phase_id === response.phase_id,
    // );
    // if (status) {
    //   response.phase.status = status;
    // }
    //
    // this._progresses.push(response);
    // this.progressesSubject.next(this._progresses);
    // await this.fetchPhasesRefactored(
    //   this.projectId,
    //   this.project_phases_params,
    // );
    this._toaster.success(this._transloco.translate('Progress added successfully'));
    return response;
  }

  async createPhase(body: any): Promise<any> {
    const response = await this._projectsApi.createNewPhase(body).toPromise();
    this._phases.push(response);
    response.weight = Number(response.weight);
    this.phasesSubject.next(this._phases);
    this.progressesSubject.next(this._progresses);
    return response;
  }

  changePhaseStatus(phase_id: string, status: 'in_progress' | 'pending' | 'closed'): void {
    const phase = this._phases.find((ph) => ph.phase_id === phase_id);
    if (phase) {
      phase.status = status;
      this.phasesSubject.next(this._phases);
      this.progressesSubject.next(this._progresses);
    }
  }

  getPhaseById(phase_id: string): PhasesApi {
    return this._phases.find((ph) => ph.phase_id === phase_id);
  }

  reset(): void {
    this._phases = [];
    this._progresses = [];
    this._phasesActivities = [];
    this.phasesSubject.next([]);
    this.phasesActivitySubject.next([]);
    this.progressesSubject.next([]);
    this.state = undefined;
    this.totalActivities = 0;
    this._progressLogs = [];
    this.progressLogsSubject.next([]);
    this.totalLogs = 0;
    this.currentPageLog = 1;
    this.remainingWeight = 0;
    this.overallPercentage = 0;
    this.projectId = '';
  }

  resetLogs(): void {
    this._progressLogs = [];
    this.progressLogsSubject.next([]);
    this.totalLogs = 0;
    this.currentPageLog = 1;
  }

  public async calculateOverallWeight(): Promise<void> {
    const res = await this.endPoint
      .get(`${this.projectId}/phases/initial-data`, null, 'projects')
      .toPromise();
    if (res) {
      this.remainingWeight = 100 - res.data.total_weight;
    }
  }

  async deletePhase(phase_id: string): Promise<boolean> {
    const body = {
      phase_id: phase_id,
      project_id: this.projectId,
    };
    const res = await this.endPoint.post('delete_phase', body).toPromise();
    if (res?.success) {
      this._phases = this._phases.filter((ph) => ph.phase_id !== phase_id);
      this.phasesSubject.next(this._phases);
      this._progresses = this._progresses.filter((pro) => pro.phase_id !== phase_id);
      this.progressesSubject.next(this._progresses);
      await this.calculateOverallWeight();
    }

    return true;
  }

  /* delete multiple or all phases*/
  async deleteMultiplePhases(body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.api_deleteMultiplePhases(body).subscribe(
        (res: any) => {
          resolve(res);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  async addActivity(activityPayload: ActivityPayload, phase_id: string): Promise<boolean> {
    try {
      const body: ActivityPayload = {
        phase_id: phase_id,
        name: activityPayload.name,
        start_date: moment(activityPayload.start_date).locale('en').format('YYYY-MM-DD'),
        end_date: moment(activityPayload.end_date).locale('en').format('YYYY-MM-DD'),
      };

      if (activityPayload.blocked_by_activity_id) {
        body.blocked_by_activity_id = activityPayload.blocked_by_activity_id;
      }

      const res = await this.endPoint.post('create_project_phase_activity', body).toPromise();

      const createPhaseActivityResponse: CreatePhaseActivityResponse =
        res as CreatePhaseActivityResponse;

      if (res?.status) {
        this._toaster.success(this._transloco.translate('Activity added successfully'));
        return true;
      } else {
        this._toaster.success(res.message);
      }
    } catch (error) {
      this._toaster.warning(this._transloco.translate('Failed to add activity'));
      console.error('Error adding activity:', error);
      return false;
    }
    return true;
  }

  async updateActivity(
    activityPayload: ActivityPayload,
    phase_id: string,
    project_id?: string,
  ): Promise<boolean> {
    try {
      const body: ActivityPayload = {
        phase_id: phase_id,
        activity_id: activityPayload.id,
        name: activityPayload.name,
        start_date: moment(activityPayload.start_date).locale('en').format('YYYY-MM-DD'),
        end_date: moment(activityPayload.end_date).locale('en').format('YYYY-MM-DD'),
      };

      if (activityPayload.blocked_by_activity_id) {
        body.blocked_by_activity_id = activityPayload.blocked_by_activity_id;
      }

      if (activityPayload.completion_percentage) {
        body.completion_percentage = activityPayload.completion_percentage;
      }

      const res = await this.endPoint.post('update_project_phase_activity', body).toPromise();

      const updatePhaseActivityResponse: CreatePhaseActivityResponse =
        res as CreatePhaseActivityResponse;

      if (res?.status) {
        this._toaster.success(this._transloco.translate('Activity updated successfully'));
        if (project_id) {
          await this.fetchPhasesActivities({
            project_id: project_id,
            sort: 'oldest',
          });
        } else {
          await this.fetchPhasesActivities({
            phase_id: phase_id,
            sort: 'oldest',
          });
        }

        return true;
      } else {
        this._toaster.success(res.message);
      }
    } catch (error) {
      this._toaster.warning(this._transloco.translate('Failed to update activity'));
      console.error('Error adding activity:', error);
      return false;
    }
    return true;
  }

  async updatePhase(body: UpdatePhaseBody): Promise<boolean> {
    const updateForm = new FormData();
    updateForm.append('phase_id', body.phase_id);
    updateForm.append('project_id', body.project_id);
    updateForm.append('phase_name', body.phase_name);
    updateForm.append('phase_desc', body.phase_desc ?? null);
    updateForm.append('weight', body.weight.toString());
    updateForm.append('planned_cost', body.planned_cost.toString());
    updateForm.append('actual_cost', body.actual_cost.toString());

    updateForm.append(
      'start_date',
      moment(body.start_date).locale('en').format('YYYY-MM-DD hh:mm:ss'),
    );
    updateForm.append('end_date', moment(body.end_date).locale('en').format('YYYY-MM-DD hh:mm:ss'));

    if (body.reports.length) {
      body.reports.map((reports: any) => updateForm.append('reports[]', reports));
    }
    body.attached?.map((attached: any) => updateForm.append('attached[]', attached));

    const res: PhasesApi = await this.endPoint.post('update_phase', updateForm).toPromise();

    if (res) {
      const selectedIndex = this._phases.findIndex((ph) => ph.phase_id === body.phase_id);
      this._phases[selectedIndex] = res;

      await this.calculateOverallWeight();

      this.phasesSubject.next(this._phases);
      // this.progressesSubject.next(this._progresses);
      return true;
    }
    return false;
  }

  async deleteAttached(attachedId: string, phaseId: string, projectId: string): Promise<boolean> {
    const res: { success: boolean } = await this.endPoint
      .post('delete_attached', { id: attachedId, project_id: projectId })
      .toPromise();
    if (res?.success) {
      const phases = this._phases.find((ph) => ph.phase_id === phaseId);
      phases.attached = phases.attached.filter((att) => att.id !== attachedId);
      this.phasesSubject.next(this._phases);
      this.progressesSubject.next(this._progresses);
    }

    return true;
  }

  fetchPhasesRefactored(projetct_id, params): Promise<PhasesApi[]> {
    this.projectId = projetct_id;
    return new Promise((resolve, reject) => {
      this.api_getProjectPhase(projetct_id, params).subscribe(
        (res: ProjectPhasesResponseModel) => {
          resolve(res.data);
          this.phases_meta_data = res.meta_data;
          this._phases = res.data;
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  private api_deleteMultiplePhases(body: any): any {
    return this.endPoint.post('bulk-delete', body, null, 'phases');
  }

  private api_getProjectPhase(projetct_id, params: any): any {
    return this.endPoint.get(`${projetct_id}/phases`, params, 'projects');
  }

  /**
   * REQ-366: Fetch all intervals for a phase.
   * GET /api/v2/phases/{phaseId}/intervals
   */
  getIntervals(phaseId: string): Observable<PhaseIntervalApi[]> {
    return this.endPoint
      .get(`${phaseId}/intervals`, null, 'phases')
      .pipe(map((res: any) => (res?.data ?? []) as PhaseIntervalApi[]));
  }

  /**
   * REQ-366: Atomically replace the phase's intervals with the given list.
   * PUT /api/v2/phases/{phaseId}/intervals
   * BE enforces strict validation; on failure the response surfaces 422 with field errors.
   */
  replaceIntervals(
    phaseId: string,
    intervals: PhaseIntervalInput[],
  ): Observable<PhaseIntervalApi[]> {
    return this.endPoint
      .put(`${phaseId}/intervals`, { intervals }, null, 'phases')
      .pipe(map((res: any) => (res?.data ?? []) as PhaseIntervalApi[]));
  }
}
